// Dinh dang manifest skin: logical key -> frame / anim / 9-slice / tile trong atlas.
// Game chi dung logical key (vd. `orb.R.basic`, `hero.aria.attack`); doi skin = doi manifest + atlas.
import { z } from 'zod';

const anchor = z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]);
const frameName = z.string().min(1);

const common = {
  /** Diem neo (0..1). Nhan vat: giua day (0.5, 1). Orb/icon: tam (0.5, 0.5). */
  anchor: anchor.default([0.5, 0.5]),
  /** true = anh chua dat (placeholder ve bang script), game van chay duoc. */
  placeholder: z.boolean().default(false),
};

export const frameEntry = z.strictObject({
  type: z.literal('frame'),
  frame: frameName,
  ...common,
});

export const animEntry = z.strictObject({
  type: z.literal('anim'),
  frames: z.array(frameName).min(1),
  fps: z.number().positive(),
  loop: z.boolean(),
  ...common,
});

export const nineSliceEntry = z.strictObject({
  type: z.literal('nineSlice'),
  frame: frameName,
  /** [trai, tren, phai, duoi] tinh bang px art */
  borders: z.tuple([z.number().int().min(0), z.number().int().min(0), z.number().int().min(0), z.number().int().min(0)]),
  ...common,
});

export const tileEntry = z.strictObject({
  type: z.literal('tile'),
  frame: frameName,
  /** lap theo truc nao */
  repeat: z.enum(['x', 'y', 'xy']),
  ...common,
});

export const manifestEntry = z.discriminatedUnion('type', [frameEntry, animEntry, nineSliceEntry, tileEntry]);

export const atlasFrame = z.strictObject({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
});

export const atlasData = z.strictObject({
  image: z.string().min(1),
  size: z.strictObject({ w: z.number().int().positive(), h: z.number().int().positive() }),
  frames: z.record(z.string(), atlasFrame),
});

export const skinManifest = z.strictObject({
  skinId: z.string().regex(/^[a-z][a-z0-9-]*$/),
  version: z.number().int().positive(),
  /** Hop tac voi Pixi: scaleMode nearest, chi phong theo he so nguyen. */
  pixelArt: z.boolean(),
  /** Duong dan tuong doi so voi manifest */
  atlases: z.array(z.string().min(1)).min(1),
  palette: z.array(z.string().regex(/^#[0-9a-f]{6}$/)).max(32),
  entries: z.record(z.string().regex(/^[a-z][A-Za-z0-9]*(\.[A-Za-z0-9]+)+$/), manifestEntry),
  /** Anh ngoai atlas (vd. icon app) */
  extras: z.record(z.string(), z.string()).default({}),
});

export type ManifestEntry = z.infer<typeof manifestEntry>;
export type AtlasData = z.infer<typeof atlasData>;
export type SkinManifest = z.infer<typeof skinManifest>;
export type AtlasFrame = z.infer<typeof atlasFrame>;

export function parseManifest(json: unknown): SkinManifest {
  return skinManifest.parse(json);
}

export function parseAtlas(json: unknown): AtlasData {
  return atlasData.parse(json);
}

/** Moi frame ma mot entry tham chieu. */
export function entryFrames(e: ManifestEntry): string[] {
  return e.type === 'anim' ? e.frames : [e.frame];
}

export class MissingAssetKeyError extends Error {
  readonly key: string;
  constructor(key: string) {
    super(`Skin thieu logical key: ${key}`);
    this.key = key;
  }
}

export interface ResolvedFrame {
  frame: string;
  rect: AtlasFrame;
  atlas: string;
}

/** Tra frame cua key. Anim tra frame theo chi so (quay vong neu loop, giu frame cuoi neu khong). */
export function resolveFrame(
  m: SkinManifest,
  atlases: Record<string, AtlasData>,
  key: string,
  frameIndex = 0,
): ResolvedFrame {
  const e = m.entries[key];
  if (!e) throw new MissingAssetKeyError(key);
  let frame: string;
  if (e.type === 'anim') {
    const n = e.frames.length;
    const i = e.loop ? ((frameIndex % n) + n) % n : Math.min(Math.max(frameIndex, 0), n - 1);
    frame = e.frames[i]!;
  } else frame = e.frame;
  for (const [name, a] of Object.entries(atlases)) {
    const rect = a.frames[frame];
    if (rect) return { frame, rect, atlas: name };
  }
  throw new Error(`Frame ${frame} (key ${key}) khong co trong atlas nao`);
}
